/**
 * usePushNotifications - Hook for managing web push notifications
 * Handles subscription, permission requests, and notification preferences
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';

// Check if push notifications are supported
const isPushSupported = () => {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
};

// Convert VAPID key from base64
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);

  // Initialize - check support and current state
  useEffect(() => {
    const init = async () => {
      // Check browser support
      const supported = isPushSupported();
      setIsSupported(supported);

      if (!supported) {
        console.log('Push notifications not supported');
        return;
      }

      // Check current permission
      setPermission(Notification.permission);

      // Fetch VAPID key from server
      try {
        const { data } = await api.get('/push/vapid-key');
        if (data.enabled && data.publicKey) {
          setVapidPublicKey(data.publicKey);
        }
      } catch (err) {
        console.log('Could not fetch VAPID key:', err);
      }

      // Check if already subscribed
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const existingSubscription = await registration.pushManager.getSubscription();
          
          if (existingSubscription) {
            setSubscription(existingSubscription);
            setIsEnabled(true);
          }
        } catch (err) {
          console.log('Error checking subscription:', err);
        }
      }
    };

    init();
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast.success('Notification permission granted');
        return true;
      } else if (result === 'denied') {
        toast.error('Notification permission denied. Please enable in browser settings.');
        return false;
      }
      return false;
    } catch (err) {
      console.error('Permission request error:', err);
      return false;
    }
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !vapidPublicKey) {
      toast.error('Push notifications are not available');
      return false;
    }

    setLoading(true);

    try {
      // Request permission if not granted
      if (permission !== 'granted') {
        const granted = await requestPermission();
        if (!granted) {
          setLoading(false);
          return false;
        }
      }

      // Register service worker if not already
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const subscriptionJSON = pushSubscription.toJSON();
      
      await api.post('/push/subscribe', {
        subscription: {
          endpoint: subscriptionJSON.endpoint,
          keys: subscriptionJSON.keys,
          expirationTime: subscriptionJSON.expirationTime
        },
        device_name: getDeviceName()
      });

      setSubscription(pushSubscription);
      setIsEnabled(true);
      toast.success('Push notifications enabled');
      
      return true;
    } catch (err) {
      console.error('Subscribe error:', err);
      toast.error('Failed to enable push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, vapidPublicKey, permission, requestPermission]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return false;

    setLoading(true);

    try {
      // Unsubscribe from browser
      await subscription.unsubscribe();

      // Remove from server
      await api.delete('/push/unsubscribe', {
        params: { endpoint: subscription.endpoint }
      });

      setSubscription(null);
      setIsEnabled(false);
      toast.success('Push notifications disabled');
      
      return true;
    } catch (err) {
      console.error('Unsubscribe error:', err);
      toast.error('Failed to disable push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, [subscription]);

  // Toggle subscription
  const toggle = useCallback(async () => {
    if (isEnabled) {
      return unsubscribe();
    } else {
      return subscribe();
    }
  }, [isEnabled, subscribe, unsubscribe]);

  // Send test notification
  const sendTest = useCallback(async () => {
    if (!isEnabled) {
      toast.error('Enable notifications first');
      return false;
    }

    try {
      const { data } = await api.post('/push/test', {
        title: 'Test Notification',
        body: 'If you see this, push notifications are working!'
      });

      if (data.success) {
        toast.success('Test notification sent');
      } else {
        toast.error(data.message || 'Failed to send test');
      }
      return data.success;
    } catch (err) {
      toast.error('Failed to send test notification');
      return false;
    }
  }, [isEnabled]);

  return {
    isSupported,
    isEnabled,
    permission,
    loading,
    subscribe,
    unsubscribe,
    toggle,
    sendTest,
    requestPermission
  };
};

// Get device name for subscription
const getDeviceName = () => {
  const ua = navigator.userAgent;
  
  if (/iPhone|iPad|iPod/.test(ua)) {
    return 'iOS Device';
  } else if (/Android/.test(ua)) {
    return 'Android Device';
  } else if (/Windows/.test(ua)) {
    return 'Windows Browser';
  } else if (/Mac/.test(ua)) {
    return 'Mac Browser';
  } else if (/Linux/.test(ua)) {
    return 'Linux Browser';
  }
  
  return 'Unknown Device';
};

export default usePushNotifications;
